export const SYNC_OPERATION = {
    CREATE: 'create',
    UPDATE: 'update',
    DELETE: 'delete',
};

/**
 * Builds a condition function for polling until a list of objects (each having a `uuid` field)
 * reflects the expected state after a CREATE, UPDATE, or DELETE operation.
 *
 * Designed for use with `pollUntil` to detect when the backend has finished syncing.
 *
 * @param {string} operation - one of {@link SYNC_OPERATION}
 * @param {string} uuid - uuid of the target item
 * @param {Object|null} [previousItem=null] - snapshot of the item before the update
 *   (required for UPDATE — used to detect when the item has actually changed)
 * @returns {Function} conditionFn(list: Array<{uuid: string, ...}>) => boolean
 *
 * @example
 * // CREATE — wait until the new item appears
 * const condition = buildListSyncCondition(SYNC_OPERATION.CREATE, newUuid);
 *
 * // UPDATE — wait until the item differs from its previous snapshot
 * const condition = buildListSyncCondition(SYNC_OPERATION.UPDATE, uuid, snapshotBefore);
 *
 * // DELETE — wait until the item is gone
 * const condition = buildListSyncCondition(SYNC_OPERATION.DELETE, uuid);
 */
export const buildListSyncCondition = (operation, uuid, previousItem = null) => {
    switch (operation) {
        case SYNC_OPERATION.CREATE:
            return (list) => list.some((item) => item.uuid === uuid);

        case SYNC_OPERATION.UPDATE:
            return (list) => {
                const item = list.find((item) => item.uuid === uuid);
                if (!item) {
                    return false;
                }

                return item?.last_modified !== previousItem?.last_modified;
            };

        case SYNC_OPERATION.DELETE:
            return (list) => !list.some((item) => item.uuid === uuid);

        default:
            return (list) => list.some((item) => item.uuid === uuid);
    }
};
